import React from "react";

const TOKEN = {
  comment: "text-slate-400 italic",
  tag: "text-rose-600",
  attr: "text-sky-700",
  string: "text-emerald-700",
  punct: "text-slate-500",
  keyword: "text-violet-600",
  plain: "text-slate-700",
} as const;

const JS_KEYWORDS = new Set([
  "window",
  "addEventListener",
  "function",
  "var",
  "if",
  "typeof",
  "document",
  "getElementById",
  "return",
  "true",
  "false",
  "null",
]);

function pushToken(nodes: React.ReactNode[], key: number, className: string, text: string) {
  if (!text) return;
  nodes.push(
    <span key={key} className={`ignore-base-styling ${className}`}>
      {text}
    </span>
  );
}

function highlightJs(code: string, startKey: number): { nodes: React.ReactNode[]; nextKey: number } {
  const nodes: React.ReactNode[] = [];
  let key = startKey;
  const re = /("(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*')|\b([A-Za-z_$][\w$]*)\b|([^"'A-Za-z_$]+)/g;
  let match: RegExpExecArray | null;

  while ((match = re.exec(code)) !== null) {
    if (match[1]) {
      pushToken(nodes, key++, TOKEN.string, match[1]);
    } else if (match[2]) {
      pushToken(nodes, key++, JS_KEYWORDS.has(match[2]) ? TOKEN.keyword : TOKEN.plain, match[2]);
    } else if (match[3]) {
      pushToken(nodes, key++, TOKEN.punct, match[3]);
    }
  }

  return { nodes, nextKey: key };
}

function highlightTag(tag: string, startKey: number): { nodes: React.ReactNode[]; nextKey: number } {
  const nodes: React.ReactNode[] = [];
  let key = startKey;
  const re = /(<\/?)([A-Za-z][\w:-]*)|([A-Za-z_:][\w:.-]*)(=)|("(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*')|(\/?>)|(\s+)/g;
  let match: RegExpExecArray | null;

  while ((match = re.exec(tag)) !== null) {
    if (match[1] !== undefined && match[2] !== undefined) {
      pushToken(nodes, key++, TOKEN.punct, match[1]);
      pushToken(nodes, key++, TOKEN.tag, match[2]);
    } else if (match[3] !== undefined && match[4] !== undefined) {
      pushToken(nodes, key++, TOKEN.attr, match[3]);
      pushToken(nodes, key++, TOKEN.punct, match[4]);
    } else if (match[5]) {
      pushToken(nodes, key++, TOKEN.string, match[5]);
    } else if (match[6]) {
      pushToken(nodes, key++, TOKEN.punct, match[6]);
    } else if (match[7]) {
      pushToken(nodes, key++, TOKEN.plain, match[7]);
    }
  }

  return { nodes, nextKey: key };
}

/** Lightweight HTML (+ inline script) highlighter for the embed iframe snippet. */
function highlightHtmlSnippet(code: string): React.ReactNode {
  if (!code) return null;

  const nodes: React.ReactNode[] = [];
  let key = 0;
  let i = 0;

  while (i < code.length) {
    if (code.startsWith("<!--", i)) {
      const end = code.indexOf("-->", i);
      const close = end === -1 ? code.length : end + 3;
      pushToken(nodes, key++, TOKEN.comment, code.slice(i, close));
      i = close;
      continue;
    }

    if (code[i] === "<") {
      const end = code.indexOf(">", i);
      if (end === -1) {
        pushToken(nodes, key++, TOKEN.plain, code.slice(i));
        break;
      }

      const tag = code.slice(i, end + 1);
      const highlighted = highlightTag(tag, key);
      nodes.push(...highlighted.nodes);
      key = highlighted.nextKey;
      i = end + 1;

      const openScript = /^<script(?:\s|>)/i.test(tag);
      if (openScript) {
        const closeIdx = code.toLowerCase().indexOf("</script>", i);
        if (closeIdx === -1) {
          const js = highlightJs(code.slice(i), key);
          nodes.push(...js.nodes);
          key = js.nextKey;
          break;
        }
        const js = highlightJs(code.slice(i, closeIdx), key);
        nodes.push(...js.nodes);
        key = js.nextKey;
        i = closeIdx;
      }
      continue;
    }

    const nextTag = code.indexOf("<", i);
    const end = nextTag === -1 ? code.length : nextTag;
    pushToken(nodes, key++, TOKEN.plain, code.slice(i, end));
    i = end;
  }

  return nodes;
}

export { highlightHtmlSnippet };
